import { useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';

import Header from 'components/layout/Header';
import { PageHeader, PageHeaderProvider } from 'components/layout/PageHeader';
import Sidebar from 'components/layout/Sidebar';
import SubscriptionBanner from 'components/layout/SubscriptionBanner';
import { resolveActiveNavItem, resolveActiveSection } from 'constants/navigation';
import { ROUTES } from 'constants/routePaths';
import { resolveInSections, visibleNav } from 'util/visibleNav';
import { useAuth } from 'hooks/useAuth';
import { isSuperAdminRole } from 'util/access';
import { formatLongDateWithWeekday } from 'util/formatters';

// Exact-path header overrides for routes whose title/subtitle shouldn't come
// from the sidebar nav item (account pages, or sub-routes like "Add Startup").
// `crumb` is the breadcrumb's first segment (reference: `Workspace > Messages`,
// `Account > My Profile`) for routes not reachable from the sidebar nav, where
// resolveActiveSection can't infer one.
const ROUTE_META = {
  '/dashboard/center-profile': {
    title: 'Profile Setup Wizard',
    subtitle: 'Complete your center profile setup',
  },
  '/dashboard/center-notification-preferences': {
    title: 'Notification Preferences',
    subtitle: 'Choose what your center gets notified about, and through which channel.',
    crumb: 'Overview',
  },
  '/dashboard/notifications': {
    title: 'Notifications',
    subtitle: 'Your alerts and updates',
    crumb: 'Overview',
  },
  '/dashboard/messages': {
    title: 'Messages',
    subtitle: 'Direct conversations with admins, mentors, and founders.',
    crumb: 'Overview',
  },
  '/dashboard/profile': {
    title: 'My Profile',
    subtitle: 'Manage your profile and security settings',
    crumb: 'Overview',
  },
  '/dashboard/organization-profile': {
    title: 'Organization Profile',
    subtitle: 'Manage your organization identity and branding',
    crumb: 'Overview',
  },
  '/dashboard/settings': {
    title: 'Settings & Activities',
    subtitle: 'Manage your settings and activities',
    crumb: 'Overview',
  },
  '/dashboard/startups/new': {
    title: 'Add Startup',
    subtitle: "Register a startup into your ANABLR program and create the founder's account.",
  },
  // The sidebar nav item stays "Workspace" (it also labels the section that
  // holds Collaborations), but the page itself reads "Projects" — both
  // overridden together so they still collapse into one breadcrumb segment
  // instead of showing "Workspace > Projects".
  '/dashboard/todos': {
    title: 'Projects',
    crumb: 'Projects',
  },
};

/** Authenticated app shell: grouped sidebar + header + routed content. */
export function DashboardLayout() {
  const { pathname } = useLocation();
  const { role, user, principal, can, permissions, hasResourceGrant } = useAuth();
  const hasCenter = Boolean(user?.center_id ?? user?.centerId ?? user?.center?.id);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  // Resolved against the modules this user actually SEES — the same list
  // util/visibleNav gives the Sidebar — so the header title, the breadcrumb and
  // the sidebar highlight cannot disagree. This used to walk the role's STATIC
  // layout, so a module the role was granted (but whose own panel never listed
  // it) produced no title and no crumb at all.
  //
  // The global resolvers remain the fallback for pages with no nav item of
  // their own — Profile, Settings, a collaboration deep link — which have never
  // been in anybody's sidebar.
  const { sections: visibleSections } = visibleNav({
    role,
    principal,
    hasCenter,
    assignments: user?.assignments,
    roleNames: user?.roles,
    can,
    hasResourceGrant,
    permsLoaded: Boolean(permissions?.permissions),
  });
  const resolved = resolveInSections(visibleSections, pathname);
  const active =
    resolved.item || resolveActiveNavItem(pathname, role, hasCenter, user?.assignments);

  const today = formatLongDateWithWeekday(new Date());
  // Settings is tabbed with the tab in the path (/dashboard/settings/activities),
  // so it resolves to the same meta as the bare route rather than falling
  // through to the sidebar's default.
  const metaPath = pathname.startsWith('/dashboard/settings/') ? '/dashboard/settings' : pathname;
  const routeMeta = ROUTE_META[metaPath];
  const subtitle =
    pathname === '/dashboard'
      ? `${today} · All systems operational`
      : routeMeta?.subtitle ?? active?.subtitle;
  // The no-access landing (no module at all, Overview included) shows no
  // title and no breadcrumb — "Overview" there named a page they can't open.
  const noModules = pathname === ROUTES.NO_MODULES;
  // The SuperAdminDashboard's platform-wide overview reads "Platform control"
  // rather than the sidebar's generic "Overview" label.
  const title = noModules
    ? ''
    : pathname === '/dashboard' && isSuperAdminRole(role)
      ? 'Platform control'
      : routeMeta?.title || active?.pageTitle || active?.label || 'Overview';
  // Dashboard root gets a single-segment breadcrumb (just the title, e.g.
  // "Overview") — no leading section, since "Dashboard > Overview" is
  // redundant at the top level. Every other page gets the full
  // "Section > Page" breadcrumb, resolved from the sidebar section the
  // active nav item lives in.
  const crumb =
    pathname === '/dashboard'
      ? null
      : routeMeta?.crumb
        ?? resolved.section
        ?? resolveActiveSection(pathname, role, hasCenter, user?.assignments);
  // The breadcrumb always reads the sidebar's generic "Overview" on the
  // dashboard root, even for Super Admin — "Platform control" stays the big
  // <h1> heading (below), it just isn't echoed in the topbar breadcrumb.
  const crumbTitle = pathname === '/dashboard' ? active?.label || 'Overview' : title;

  return (
    <div className="flex h-screen overflow-hidden bg-paper text-ink">
      <Sidebar open={mobileNavOpen} onClose={() => setMobileNavOpen(false)} />
      <div className="flex min-w-0 flex-1 flex-col">
        <PageHeaderProvider>
          <SubscriptionBanner />
          <Header onMenuClick={() => setMobileNavOpen(true)} crumb={crumb} title={crumbTitle} />
          <main className="min-w-0 flex-1 overflow-y-auto px-4 py-5 sm:px-6 lg:px-10 lg:py-6">
            <PageHeader title={title} subtitle={subtitle} heroStyle={pathname === '/dashboard'} />
            <Outlet />
          </main>
        </PageHeaderProvider>
      </div>
    </div>
  );
}

export default DashboardLayout;
